import { useState, useEffect } from 'react';
import { plantsApi } from '../services/api';

// Module-level cache (shared across all instances, reset on page reload)
let lawnTypesCache = null;
let fetchPromise = null;

/**
 * Hook to fetch and cache lawn types from the plant database.
 * Filters plants with properties.growth_type === 'lawn'.
 * Uses module-level cache so the API is called only once per session.
 *
 * @returns {{ lawnTypes: Array, loading: boolean, error: string|null }}
 */
export function useLawnTypes() {
  const [lawnTypes, setLawnTypes] = useState(lawnTypesCache || []);
  const [loading, setLoading] = useState(!lawnTypesCache);
  const [error, setError] = useState(null);

  useEffect(() => {
    // State is already seeded from the cache via the lazy initializers above, so
    // there is nothing to fetch or set synchronously when the cache is warm.
    if (lawnTypesCache) return;

    const fetchLawnTypes = async () => {
      // Deduplicate concurrent requests
      if (!fetchPromise) {
        fetchPromise = plantsApi.getFullCatalog();
      }

      try {
        const allPlants = await fetchPromise;
        const lawns = (allPlants || [])
          .filter(p => p.properties?.growth_type === 'lawn')
          .map(p => ({
            value: p.name.toLowerCase().replace(/\s+/g, '_'),
            label: p.name,
            latinName: p.scientific_name || '',
            notes: p.properties?.additional_properties?.notes || '',
            careNotes: p.care_instructions?.note_short || '',
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'de'));

        lawnTypesCache = lawns;
        setLawnTypes(lawns);
      } catch (err) {
        console.error('Failed to fetch lawn types:', err);
        setError('Rasensorten konnten nicht geladen werden.');
      } finally {
        setLoading(false);
        fetchPromise = null;
      }
    };

    fetchLawnTypes();
  }, []);

  return { lawnTypes, loading, error };
}

/**
 * Map of legacy values from old static config to new DB-based values.
 */
const LEGACY_VALUE_MAP = {
  wildblumenwiese: 'blumenwiese',
};

/**
 * Get lawn type info by value (for displaying care notes).
 * Also handles legacy values via LEGACY_VALUE_MAP.
 */
export function getLawnTypeInfo(lawnTypes, value) {
  if (!value) return null;
  const mappedValue = LEGACY_VALUE_MAP[value] || value;
  return lawnTypes.find(lt => lt.value === mappedValue) || null;
}

/**
 * Resolve a potentially legacy value to the current DB-based value.
 * Returns the mapped value if a legacy mapping exists, otherwise the original.
 */
export function resolveLawnTypeValue(value) {
  if (!value) return value;
  return LEGACY_VALUE_MAP[value] || value;
}

// For testing: reset module-level cache
export function _resetLawnTypesCache() {
  lawnTypesCache = null;
  fetchPromise = null;
}
